// Live Etsy catalog (October 2026 launch). Single source of truth for the /shop
// grid, the /shop/:slug product pages, the sitemap and the static SEO HTML.
// Plain data only (no JSX) so the Node build scripts can import it.
// Media lives in /public/shop/<slug>/: cover.jpg|webp, cover-600.jpg|webp,
// page-N.jpg|webp and walkthrough.mp4 (silent, 720x720).

export const SITE_SHOP_LAUNCH = '2026-10-23';

export const CATALOG = [
  {
    "slug": "built-nervous-system-course",
    "etsyId": "4599942161",
    "key": "built-nervous-system",
    "name": "The Built Nervous System",
    "seoTitle": "Nervous System Regulation Course for Your Home (5 PDFs)",
    "category": "Lifestyle & Environment",
    "tag": "COURSE · ENVIRONMENT",
    "coverBg": "from-slate-800 to-slate-950",
    "price": "$29.99",
    "format": "5 PDF files, about 120 pages in total (US Letter and A4)",
    "metaDescription": "This is a five-file course that shows you how to set up your home so your nervous system can settle. Light, noise, air, temperature, clutter and where.",
    "keywords": [
      "environment design",
      "sleep hygiene",
      "home sanctuary",
      "anxiety relief",
      "stress relief",
      "circadian rhythm",
      "habit tracker",
      "self help course",
      "wellness bundle",
      "burnout recovery",
      "cozy home ideas",
      "neuroscience",
      "instant download"
    ],
    "intro": [
      "This is a five-file course that shows you how to set up your home so your nervous system can settle. Light, noise, air, temperature, clutter and where your phone sleeps all affect how calm, focused and rested you feel, and this course takes you through each one, room by room.",
      "Most changes use things you already own. Where a purchase helps, the blueprints give you three budget levels so you can start small."
    ],
    "inside": [
      "The main course, 61 pages. Seven modules: the outsourcing principle, your sensory budget, light, sound, air and temperature, nature and visual space, and digital friction. Includes 8 diagrams, 29 references and QR codes for the free Recalibrate app.",
      "Room blueprints, 19 pages. Bedroom, workspace, kitchen, entryway and bathroom, each with a layout map and £20, £100 and £600 options.",
      "The 21-day install plan, 15 pages. One small change a day, about 10 minutes each, with a wall tracker.",
      "The environment deck, 12 pages. 30 cards to print and cut, each with one swap and the study behind it.",
      "The sensory audit worksheet, 12 pages. Score each room with no special equipment."
    ],
    "whoFor": [
      "Anyone whose evenings feel wired and whose sleep never quite resets",
      "People who notice noise, light and clutter more than most",
      "Anyone rebuilding from stress, burnout or a long illness",
      "Renters, since almost everything works without changing the building"
    ],
    "closing": [
      "Every recommendation explains why it works and points to the research, with 40+ cited studies across the five files. Created by the Recalibrate team with AI tools used for research support, layout and some interior images, and checked against the cited studies.",
      "Start with the audit this weekend and you'll know which room to fix first."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2",
      "page-3",
      "page-4",
      "page-5"
    ],
    "videoSeconds": 15,
    "series": null
  },
  {
    "slug": "window-of-tolerance-journal",
    "etsyId": "4600000366",
    "key": "window-journal",
    "name": "Window of Tolerance Journal",
    "seoTitle": "Window of Tolerance Journal: 7-Day Nervous System Tracker",
    "category": "Self-Management",
    "tag": "JOURNAL · 7 DAYS",
    "coverBg": "from-violet-300 to-purple-500",
    "price": "$1.99",
    "format": "12-page A4 PDF",
    "metaDescription": "This window of tolerance journal is a 7-day tracker for noticing what state your nervous system is in. Three quick check-ins a day show you, by the end.",
    "keywords": [
      "polyvagal",
      "anxiety journal",
      "emotion tracker",
      "therapy worksheet",
      "symptom tracker",
      "self care journal",
      "journal prompts",
      "mental health",
      "feelings journal",
      "somatic healing",
      "trauma informed",
      "daily check in",
      "instant download"
    ],
    "intro": [
      "This window of tolerance journal is a 7-day tracker for noticing what state your nervous system is in. Three quick check-ins a day show you, by the end of the week, when you tend to feel safe, wired or shut down, and what brings you back.",
      "Each check-in takes about a minute: circle your state, note one body clue and one thing that helped. The summary page turns the week into a clear picture you can bring to a therapist or doctor."
    ],
    "inside": [
      "A short page on why naming your state helps",
      "The legend: safe, mobilised and shutdown, with the body signs of each and space for your own",
      "7 daily pages with morning, midday and evening check-ins and a different end-of-day prompt each day",
      "A pattern-finder: your most common state, your main triggers, what helps and the story of your week"
    ],
    "whoFor": [
      "Anyone who finds it hard to tell what they're feeling until it's too much",
      "People in therapy who want real notes to bring to sessions",
      "Anyone living with anxiety, stress, chronic pain or burnout who wants to see their patterns"
    ],
    "closing": [
      "Start on any day of the week. Seven days is enough to see something you didn't know."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": null
  },
  {
    "slug": "nervous-system-reset-starter-kit",
    "etsyId": "4599981011",
    "key": "starter-kit",
    "name": "Nervous System Reset Starter Kit",
    "seoTitle": "Nervous System Reset Starter Kit: Beginner Guide and 7-Day Plan",
    "category": "Neuroscience",
    "tag": "STARTER KIT · BEGINNERS",
    "coverBg": "from-indigo-500 to-purple-700",
    "price": "$0.99",
    "format": "13-page A4 PDF",
    "metaDescription": "This nervous system reset starter kit is a short beginner guide for when your body feels stuck on high alert. It explains what your nervous system is.",
    "keywords": [
      "vagus nerve",
      "anxiety relief",
      "stress relief",
      "self care plan",
      "chronic pain",
      "chronic illness",
      "mental health",
      "wellness guide",
      "burnout recovery",
      "somatic healing",
      "polyvagal",
      "healing journey",
      "instant download"
    ],
    "intro": [
      "This nervous system reset starter kit is a short beginner guide for when your body feels stuck on high alert. It explains what your nervous system is doing in plain language, then gives you five simple practices and a 7-day plan to start with.",
      "It is deliberately short. You can read it in one sitting and start the plan the same day."
    ],
    "inside": [
      "Your nervous system, explained in four ideas",
      "The daily foundations: rest, food, movement, brain anchors, learning and connection",
      "Five practices that work, from longer exhales to gentle movement",
      "The Recalibrate Compass, a quick way to choose your next step on a hard day",
      "A 7-day reset plan with tick boxes",
      "An explorer's log with reflection prompts"
    ],
    "whoFor": [
      "Anyone new to nervous system regulation who wants a clear place to start",
      "People living with stress, anxiety, chronic pain or a long-term illness",
      "Anyone who wants a gentle one-week plan rather than another long book"
    ],
    "closing": [
      "Download it now and start day one tomorrow morning."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": null
  },
  {
    "slug": "3am-wake-up-guide",
    "etsyId": "4599977477",
    "key": "mini-01-three-am-wakeup",
    "name": "The 3 a.m. Wake-Up Reset",
    "seoTitle": "3am Wake Up Guide: What To Do When You Can’t Fall Back Asleep",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · SLEEP",
    "coverBg": "from-indigo-900 to-purple-900",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This is a short printable guide for the nights you wake at 3 a.m. with a racing mind and can't get back to sleep. It explains why that hour is so.",
    "keywords": [
      "insomnia help",
      "sleep anxiety",
      "night waking",
      "sleep routine",
      "racing thoughts",
      "anxiety relief",
      "nervous system",
      "bedtime routine",
      "self help pdf",
      "stress relief",
      "coping skills",
      "sleep hygiene",
      "instant download"
    ],
    "intro": [
      "This is a short printable guide for the nights you wake at 3 a.m. with a racing mind and can't get back to sleep. It explains why that hour is so common and gives you a calm, step-by-step plan for the next 20 minutes.",
      "Keep it by the bed. The steps are short enough to follow half asleep."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Six step-by-step tools: paradoxical lying, the physiological sigh, 4:6 breathing, warm shower and cool room, jaw and tongue release, and 5-4-3-2-1 grounding",
      "What to do in the moment: why to leave the clock alone, when to get up, and what to avoid",
      "A 7-night wake-up log",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone who wakes between 2 and 4 a.m. and lies there thinking",
      "People whose sleep falls apart during stressful weeks",
      "Anyone who wants a plan that keeps them off their phone at night"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Print it tonight and put it on your bedside table."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "panic-attack-guide",
    "etsyId": "4599977899",
    "key": "mini-02-panic-first-aid",
    "name": "Panic Attack First Aid",
    "seoTitle": "Panic Attack Guide with Wallet Card: Step-by-Step Calm Down Plan",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · PANIC",
    "coverBg": "from-rose-800 to-red-900",
    "price": "$1.99",
    "format": "13-page A5 PDF",
    "metaDescription": "This panic attack guide gives you a step-by-step plan for the worst minutes of a panic attack, written so you can follow it when you can barely think..",
    "keywords": [
      "anxiety relief",
      "grounding",
      "coping skills",
      "mental health",
      "anxiety toolkit",
      "nervous system",
      "breathing exercise",
      "therapy worksheet",
      "self help pdf",
      "stress relief",
      "calm down tools",
      "panic disorder",
      "instant download"
    ],
    "intro": [
      "This panic attack guide gives you a step-by-step plan for the worst minutes of a panic attack, written so you can follow it when you can barely think. It includes a wallet card to cut out and carry, so the first steps are always with you.",
      "The guide follows a panic attack in three stages (the first minute, the peak and the come-down) and gives you one or two tools for each."
    ],
    "inside": [
      "What is happening in your body, and why it always ends",
      "An emergency script to read slowly, in order, mid-attack",
      "Stage 1, the first 60 seconds: the physiological sigh",
      "Stage 2, the peak: 5-4-3-2-1 grounding and riding it out",
      "Stage 3, the come-down: gentle tools for the next 30 minutes",
      "What to do alone, and how someone with you can help",
      "Common moves that make panic worse",
      "A wallet card to print and cut out",
      "A 7-day attack log and a page to build your own panic plan"
    ],
    "whoFor": [
      "Anyone who has had a panic attack and is afraid of the next one",
      "People who want a plan on paper they can hold mid-attack",
      "Partners, friends and parents who want to know how to help"
    ],
    "closing": [
      "Print it now and put the wallet card in your bag or phone case today."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "sunday-scaries-guide",
    "etsyId": "4600002130",
    "key": "mini-03-sunday-scaries",
    "name": "The Sunday Scaries Protocol",
    "seoTitle": "Sunday Scaries Guide: Calm Sunday Night Anxiety Before the Work Week",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · ANXIETY",
    "coverBg": "from-rose-300 to-orange-300",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This printable guide is a plan for Sunday night anxiety, the dread that starts on Sunday afternoon and builds until bedtime. It explains why your brain.",
    "keywords": [
      "sunday blues",
      "monday dread",
      "evening routine",
      "weekend reset",
      "work stress",
      "anxiety relief",
      "nervous system",
      "self care routine",
      "coping skills",
      "burnout recovery",
      "self help pdf",
      "worry journal",
      "instant download"
    ],
    "intro": [
      "This printable guide is a plan for Sunday night anxiety, the dread that starts on Sunday afternoon and builds until bedtime. It explains why your brain rehearses Monday early and gives you a short weekend routine that closes those loops.",
      "It takes under an hour across the whole weekend, and most of it is done on Saturday."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: the worry window, a nature dose, coherent breathing, a 10-minute evening anchor and phone away for the last hour",
      "A weekend timeline from a Saturday brain dump to a Sunday walk and a quiet 9 p.m. wind-down",
      "A 4-week Sunday scaries tracker",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone who loses Sunday evening to thinking about Monday",
      "People in demanding jobs or a new role",
      "Anyone who wants their weekend back"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it now and try it this weekend."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "presentation-nerves-guide",
    "etsyId": "4600002696",
    "key": "mini-04-pre-meeting-reset",
    "name": "Pre-Meeting Reset",
    "seoTitle": "Presentation Nerves Guide: 8-Minute Calm Routine Before Meetings",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · NERVES",
    "coverBg": "from-slate-200 to-indigo-300",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This is an 8-minute routine for the nerves before a presentation, interview or important meeting. It works on the body first (breath, posture, jaw and.",
    "keywords": [
      "public speaking",
      "interview tips",
      "performance anxiety",
      "work anxiety",
      "confidence tips",
      "anxiety relief",
      "nervous system",
      "breathing exercise",
      "career gift",
      "coping skills",
      "self help pdf",
      "work stress",
      "instant download"
    ],
    "intro": [
      "This is an 8-minute routine for the nerves before a presentation, interview or important meeting. It works on the body first (breath, posture, jaw and voice) so the material you prepared comes out the way you practised it.",
      "You can do all of it in a bathroom, a corridor or a parked car."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: the physiological sigh, coherent breathing, jaw and tongue release, shoulder drop, box breathing and a quick state check",
      "The minute-by-minute 8-minute sequence, plus what to do if you spike mid-meeting",
      "A meeting reset log",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone with a big presentation, pitch or interview coming up",
      "People whose voice shakes or mind goes blank under pressure",
      "Students before exams and vivas"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it today and run through it once before you need it."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "afternoon-slump-guide",
    "etsyId": "4600003268",
    "key": "mini-05-energy-cliff",
    "name": "The 4 p.m. Energy Cliff",
    "seoTitle": "Afternoon Slump Guide: Beat the 3pm Energy Crash Without More Coffee",
    "category": "Self-Management",
    "tag": "MINI GUIDE · ENERGY",
    "coverBg": "from-orange-300 to-amber-500",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This printable guide is a 10-minute reset for the afternoon slump, the 3 to 4 p.m. energy crash most people answer with coffee or sugar. It explains.",
    "keywords": [
      "low energy",
      "fatigue help",
      "focus routine",
      "productivity",
      "work from home",
      "caffeine detox",
      "nervous system",
      "burnout recovery",
      "healthy habits",
      "self help pdf",
      "stress relief",
      "office wellness",
      "instant download"
    ],
    "intro": [
      "This printable guide is a 10-minute reset for the afternoon slump, the 3 to 4 p.m. energy crash most people answer with coffee or sugar. It explains what is actually happening in your body and gives you a calmer way back to focus that doesn't wreck your evening.",
      "It also covers the lunch and caffeine choices that make the crash worse."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: morning light, panoramic gaze, cold water on the face, shake-off, walking meditation and box breathing",
      "An hour-by-hour plan from lunch to the end of the working day, including how to use caffeine well",
      "An afternoon crash log",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone whose afternoons fall apart around 3 p.m.",
      "People working from home without a natural break",
      "Anyone stuck in a several-coffees-a-day loop"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it now and try the reset this afternoon."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "after-an-argument-guide",
    "etsyId": "4600003716",
    "key": "mini-06-after-a-fight",
    "name": "After-a-Fight Recovery",
    "seoTitle": "Calm Down After an Argument: Recovery Guide After a Fight",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · CONFLICT",
    "coverBg": "from-pink-300 to-rose-500",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This guide helps you calm down after an argument with a partner, parent, friend or colleague. Your body can stay on alert for hours after a fight, and.",
    "keywords": [
      "relationship help",
      "conflict recovery",
      "couples therapy",
      "emotional reset",
      "anxiety relief",
      "nervous system",
      "self compassion",
      "coping skills",
      "communication",
      "self help pdf",
      "stress relief",
      "marriage help",
      "instant download"
    ],
    "intro": [
      "This guide helps you calm down after an argument with a partner, parent, friend or colleague. Your body can stay on alert for hours after a fight, and this walks you through the first hour, the rest of the day and the next morning.",
      "It is about settling your own system first, so that any repair conversation goes better."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: shake-off, walking meditation, a self-compassion break, two-hand soothe, a co-regulation call and safe figure visualisation",
      "A recovery timeline: the first hour, one to four hours later, and the next morning",
      "A recovery log",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone whose body holds onto an argument for days",
      "People who rush to fix things and end up in round two",
      "Anyone who shuts down after conflict and wants a way back"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it now so it is there the next time you need it."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "anger-calm-down-guide",
    "etsyId": "4599980241",
    "key": "mini-07-rage-in-60-seconds",
    "name": "Rage in 60 Seconds",
    "seoTitle": "Anger Management Guide: Calm Down in 60 Seconds",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · ANGER",
    "coverBg": "from-orange-500 to-red-600",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This anger management guide gives you a 60-second plan for the moment anger spikes, before you say or do something you regret. It uses fast physical.",
    "keywords": [
      "anger help",
      "emotional regulation",
      "parenting help",
      "temper control",
      "coping skills",
      "anxiety relief",
      "nervous system",
      "breathing exercise",
      "dbt skills",
      "self help pdf",
      "stress relief",
      "mindful parenting",
      "instant download"
    ],
    "intro": [
      "This anger management guide gives you a 60-second plan for the moment anger spikes, before you say or do something you regret. It uses fast physical tools first, because in that moment your thinking brain is a step behind your body.",
      "It also shows you how to leave a room well and what to do in the 30 minutes afterwards."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: the physiological sigh, shake-off, cold water on the face, jaw and tongue release, 4:6 breathing and body-first naming",
      "A second-by-second plan from the first signs in your body to four sentences you can say",
      "An anger log for trigger, response and repair",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone with a short fuse who wants a plan they decided on in advance",
      "Parents who want to stop shouting",
      "Anyone going through a stressful season and noticing more anger than usual"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it today and read it once while you're calm."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  },
  {
    "slug": "stop-overthinking-guide",
    "etsyId": "4600004512",
    "key": "mini-08-worry-window",
    "name": "The Worry Window",
    "seoTitle": "Stop Overthinking Guide: The Daily Worry Time Method",
    "category": "Mind-Body Techniques",
    "tag": "MINI GUIDE · WORRY",
    "coverBg": "from-emerald-100 to-slate-300",
    "price": "$1.99",
    "format": "10-page A5 PDF",
    "metaDescription": "This guide teaches the worry window, a simple daily worry time method for overthinking. Instead of trying to stop worrying, you give it a set 15 to 20.",
    "keywords": [
      "worry journal",
      "rumination",
      "racing thoughts",
      "cbt worksheet",
      "anxiety relief",
      "mental health",
      "nervous system",
      "coping skills",
      "therapy worksheet",
      "self help pdf",
      "stress relief",
      "mindfulness",
      "instant download"
    ],
    "intro": [
      "This guide teaches the worry window, a simple daily worry time method for overthinking. Instead of trying to stop worrying, you give it a set 15 to 20 minutes each day, and outside that time you have a plan for what to do when a worry turns up.",
      "Scheduled worry time is a well-studied technique from cognitive behavioural therapy, set out here as a printable daily practice."
    ],
    "inside": [
      "Why it happens, explained in plain language, with a diagram",
      "Step-by-step tools: the worry window, 5-4-3-2-1 grounding, body-first naming, a self-compassion break and panoramic gaze",
      "How to set it up: the time and place, the three-column page and what to expect on days 4 to 7",
      "A 14-day worry window log",
      "A page on limits and when to get more help"
    ],
    "whoFor": [
      "Anyone who overthinks all day or lies awake replaying things",
      "People who want something to practise between therapy sessions",
      "Anyone who has tried 'just stop worrying' and found it doesn't work"
    ],
    "closing": [
      "Part of the Calming the Nervous System series, short guides that each cover one specific moment.",
      "Download it now and pick your worry time for tomorrow."
    ],
    "images": [
      "cover",
      "page-1",
      "page-2"
    ],
    "videoSeconds": 12,
    "series": "Calming the Nervous System mini guides"
  }
];

// Etsy "buy together" offers. The discount applies automatically in the Etsy
// basket when a buyer adds every item in the bundle.
export const BUNDLES = [
  { id: 'calm-under-pressure', name: 'Calm Under Pressure', discount: 30, items: ['panic-attack-guide', 'anger-calm-down-guide', 'presentation-nerves-guide'],
    blurb: 'Three fast plans for the moments your body goes into alarm: a panic attack, a flash of anger and the minutes before a big meeting.' },
  { id: 'sleep-and-worry', name: 'Sleep and Worry', discount: 30, items: ['3am-wake-up-guide', 'sunday-scaries-guide', 'stop-overthinking-guide'],
    blurb: 'For the racing mind: the 3 a.m. wake-up, the Sunday night dread and the overthinking that follows you through the day.' },
  { id: 'daily-reset', name: 'Daily Reset', discount: 30, items: ['afternoon-slump-guide', 'after-an-argument-guide', 'window-of-tolerance-journal'],
    blurb: 'The ordinary hard parts of a day: the afternoon crash, the hour after an argument and a week of noticing what state you are in.' },
  { id: 'start-here', name: 'Start Here', discount: 20, items: ['built-nervous-system-course', 'nervous-system-reset-starter-kit'],
    blurb: 'The beginner starter kit and the full home-environment course, for people who want both the foundations and the deep dive.' },
];

export const etsyListingUrl = (p) => `https://www.etsy.com/listing/${p.etsyId}`;
export const productPath = (p) => `/shop/${p.slug}`;
export const mediaPath = (p, file) => `/shop/${p.slug}/${file}`;
export const getCatalogProduct = (slug) => CATALOG.find((p) => p.slug === slug) || null;
export const bundleFor = (slug) => BUNDLES.find((b) => b.items.includes(slug)) || null;
export const bundlePrice = (b) => {
  const full = b.items.reduce((sum, s) => sum + Number(getCatalogProduct(s).price.replace('$', '')), 0);
  return { full: full.toFixed(2), now: (Math.round(full * (100 - b.discount)) / 100).toFixed(2) };
};
